import { Loader } from "@blox/design-system";
import Bugsnag from "@bugsnag/js";
import { UserManager } from "oidc-client-ts";
import queryString from "query-string";
import { ReactNode, useEffect } from "react";
import { useAuth } from "react-oidc-context";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";

import { AppContent, AppWrapper } from "./App.style";
import configParams from "./env";
import About from "./screens/About/About";
import Login from "./screens/Login/Login";

function QuickLogin({ username, password }: { username: string; password: string }) {
  const { isAuthenticated, settings, signoutRedirect } = useAuth();

  useEffect(() => {
    (async () => {
      if (isAuthenticated) {
        await signoutRedirect({
          post_logout_redirect_uri: window.location.toString(),
        });
      } else {
        const userManager = new UserManager(settings);
        await userManager.signinResourceOwnerCredentials({
          username,
          password,
        });

        window.location.assign("/");
      }
    })();
  }, [isAuthenticated, password, settings, signoutRedirect, username]);

  return <Loader variant="fullScreen" />;
}

function Auth({ children }: { children: ReactNode }) {
  const location = useLocation();
  const { isLoading, isAuthenticated, removeUser, events, user } = useAuth();
  const searchParams = new URLSearchParams(location.search);

  useEffect(() => {
    if (user?.profile.sub) {
      Bugsnag.setUser(user.profile.sub);
    }
  }, [user?.profile.sub]);

  useEffect(() => {
    return events.addSilentRenewError(removeUser);
  }, [events, removeUser]);

  if (isLoading) {
    return <Loader variant="fullScreen" />;
  }

  if (
    configParams.ENVIRONMENT === "STAGING" &&
    searchParams.has("username") &&
    searchParams.has("password")
  ) {
    return <QuickLogin username={searchParams.get("username")!} password={searchParams.get("password")!} />;
  }

  if (!isAuthenticated) {
    return (
      <Routes>
        <Route
          path="/login"
          element={
            <AppWrapper>
              <Login />
            </AppWrapper>
          }
        />
        <Route
          path="/about"
          element={
            <AppContent>
              <About />
            </AppContent>
          }
        />
        <Route
          path="*"
          element={
            <Navigate
              to={{
                pathname: "/login",
                search: queryString.stringify({ redirectUrl: window.location.toString() }),
              }}
              replace
            />
          }
        />
      </Routes>
    );
  }

  return (
    <Routes>
      <Route path="/login" element={<Navigate to="/" replace />} />
      <Route path="*" element={children} />
    </Routes>
  );
}

export default Auth;
